import React, { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip
} from 'recharts';
import { useAuth } from '../services/auth';
import { safeFetch } from '../utils/fetchJson';
import { severityColors, statusColors, ROLE_LEVEL, Gauge, Sparkline, MetricBar, formatBytes, formatBits } from './components/DashboardShared';
import DashboardSecurity from './components/DashboardSecurity';
import DashboardAI from './components/DashboardAI';
import DashboardIngestion from './components/DashboardIngestion';
import DashboardPerformance from './components/DashboardPerformance';
import DashboardServers from './components/DashboardServers';
import DashboardSources from './components/DashboardSources';
import { formatTime } from '../utils/formatTime';
import {
  KpiGrid, KpiCard, Panel, DataTable, Badge, SevBadge, Button, ChipGroup, PageHeader, AlertItem, ProgressBar,
} from '../components/design';
import { useToast } from '../components/design';
import Icon from '../components/Icon';

// ─── Main Dashboard ───
export default function Dashboard() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const toast = useToast();
  const canEdit = (ROLE_LEVEL[user?.role] ?? -1) >= ROLE_LEVEL.engineer;
  const [timeRange, setTimeRange] = useState('24h');
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [refreshInterval, setRefreshInterval] = useState(30);
  const [diskHistory, setDiskHistory] = useState([]);
  const [logoUrl, setLogoUrl] = useState(null);
  const [incidentList, setIncidentList] = useState([]);
  const [servicesExpanded, setServicesExpanded] = useState(() => {
    try { return localStorage.getItem('synoti:dash:servicesExpanded') === 'true'; } catch (e) { return false; }
  });

  // ─── react-query: logo config ───
  const { data: logoData } = useQuery({
    queryKey: ['config-logo'],
    queryFn: () => fetch('/api/config/logo').then(r => r.ok ? r.json() : null),
    staleTime: 300000,
    retry: false,
  });
  useEffect(() => { if (logoData?.logo) setLogoUrl(logoData.logo); }, [logoData]);

  // ─── react-query: core APIs with 20s staleTime (matches backend Redis cache) ───
  const refetchMs = autoRefresh ? refreshInterval * 1000 : false;
  const { data: health, isLoading: healthLoading } = useQuery({ queryKey: ['dashboard', 'health'], queryFn: () => safeFetch('/api/health'), staleTime: 20000, refetchInterval: refetchMs, refetchOnMount: true });
  const { data: status } = useQuery({ queryKey: ['dashboard', 'status'], queryFn: () => safeFetch('/api/status'), staleTime: 20000, refetchInterval: refetchMs, refetchOnMount: true });
  const { data: statsData } = useQuery({ queryKey: ['dashboard', 'container-stats', timeRange], queryFn: () => safeFetch(`/api/containers/stats?range=${timeRange}`), staleTime: 10000, refetchInterval: refetchMs, refetchOnMount: true });
  const { data: incidentsData, isLoading: incidentsLoading } = useQuery({ queryKey: ['dashboard', 'incidents', 5], queryFn: () => safeFetch('/api/incidents?limit=5'), staleTime: 20000, refetchInterval: refetchMs, refetchOnMount: true });
  const { data: serversData } = useQuery({ queryKey: ['dashboard', 'servers'], queryFn: () => safeFetch('/api/servers'), staleTime: 20000, refetchInterval: refetchMs, refetchOnMount: true });
  const { data: dashboardData } = useQuery({ queryKey: ['dashboard', 'dashboard', timeRange], queryFn: () => safeFetch(`/api/dashboard?range=${timeRange}`), staleTime: 15000, refetchInterval: refetchMs, refetchOnMount: true });
  const { data: enhancedData, dataUpdatedAt: enhancedTs, isLoading: enhancedLoading } = useQuery({ queryKey: ['dashboard', 'enhanced', timeRange], queryFn: () => safeFetch(`/api/dashboard/enhanced?range=${timeRange}`), staleTime: 20000, refetchInterval: refetchMs, refetchOnMount: true });
  const { data: forecastData } = useQuery({ queryKey: ['dashboard', 'forecast'], queryFn: () => safeFetch('/api/dashboard/forecast?resource=all&history_days=30&forecast_days=7'), staleTime: 60000, refetchInterval: 120000, refetchOnMount: true });
  const { data: agentsRegistry } = useQuery({ queryKey: ['agents-registry'], queryFn: () => safeFetch('/api/agents/registry'), staleTime: 120000, refetchInterval: 120000, refetchOnMount: true });

  // ─── Derive incident list ───
  useEffect(() => { if (incidentsData) { const d = incidentsData; setIncidentList(Array.isArray(d) ? d : d.incidents || []); } }, [incidentsData]);

  // ─── Accumulate disk history ───
  useEffect(() => {
    if (!enhancedData) return;
    const dskIO = enhancedData.disk_io || {};
    const now = formatTime(new Date().toISOString(), { timeOnly: true });
    setDiskHistory(prev => { const n = [...prev, { time: now, read: dskIO.read_bps || 0, write: dskIO.write_bps || 0 }]; return n.slice(-60); });
  }, [enhancedData]);

  const loading = !health && !dashboardData && !enhancedData;

  // ─── Derived data ───
  const containerStats = statsData?.containers || [];
  const services = containerStats.map(c => ({ name: c.name, status: c.status === 'running' ? 'running' : c.status, health: c.health, cpu: c.cpu_percent, memory: c.memory_percent, memoryUsage: c.memory_usage_mb, memoryLimit: c.memory_limit_mb, pids: c.pids, uptime: c.uptime_seconds, networkRx: c.network_rx_mb, networkTx: c.network_tx_mb, blockRead: c.block_read_mb, blockWrite: c.block_write_mb }));
  const runningCount = services.filter(s => s.status === 'running').length;
  const totalCount = services.length;
  const servers = serversData?.servers || [];
  const serverCount = servers.length;
  const onlineServers = servers.filter(s => s.status === 'online' || s.status === 'reachable').length;
  const sysMetrics = dashboardData?.system || {};
  const workers = dashboardData?.workers || {};
  const queues = dashboardData?.queues || {};
  const enhanced = enhancedData || {};
  const networkIO = enhanced.network_io || {};
  const networkIO24h = enhanced.network_io_24h || [];
  const diskIO = enhanced.disk_io || {};
  const incidentTimeline = enhanced.incident_timeline || [];
  const topAlerting = enhanced.top_alerting_servers || [];
  const uptimeSummary = enhanced.uptime_summary || [];
  const aiStats = enhanced.ai_stats || {};
  const logVolume = enhanced.log_volume || [];
  const queueDepth = enhanced.queue_depth_history || [];
  const securityEvents = enhanced.security_events || {};
  const forecast = enhanced.resource_forecast || {};
  const activityFeed = enhanced.activity_feed || [];
  const chatopsStats = enhanced.chatops_stats || {};
  const dbSizes = enhanced.database_sizes || {};
  const mttrTrend = enhanced.mttr_trend || [];
  const mttrStats = (() => {
    const totalCount = mttrTrend.reduce((s, d) => s + (d.count || 0), 0);
    const totalSeconds = mttrTrend.reduce((s, d) => s + (d.count || 0) * (d.avg_seconds || 0), 0);
    if (!totalCount || !totalSeconds) return null;
    const avg = totalSeconds / totalCount;
    if (avg < 60) return `${Math.round(avg)}s`;
    if (avg < 3600) return `${(avg / 60).toFixed(1)}m`;
    return `${(avg / 3600).toFixed(1)}h`;
  })();
  const severityTimeline = enhanced.incident_severity_timeline || [];
  const aiEffectiveness = enhanced.ai_effectiveness || [];
  const kafkaLagRaw = enhanced.kafka_lag || [];
  const threatsBlocked = enhanced.threats_blocked || {};
  const sourceStatus = enhanced.source_status || [];
  // Backend should always return consistent shape: { lag: number }
  const kafkaLag = kafkaLagRaw.lag || 0;
  const components = health?.components || {};

  const allExporters = servers.flatMap(server => server.exporters || []);
  const exporterNames = [...new Set(allExporters.map(e => e.name))].sort();
  const totalAgentTypes = agentsRegistry?.agent_names?.length || exporterNames.length;
  const GRADIENTS = ['from-green-500 to-emerald-600','from-blue-500 to-indigo-600','from-orange-500 to-amber-600','from-red-500 to-rose-600','from-teal-500 to-cyan-600','from-sky-500 to-blue-600','from-violet-500 to-purple-600','from-fuchsia-500 to-pink-600','from-lime-500 to-green-600','from-cyan-500 to-teal-600','from-amber-500 to-yellow-600','from-slate-500 to-gray-600','from-rose-500 to-red-600','from-yellow-500 to-orange-600','from-pink-500 to-rose-500'];
  const exporterCounts = exporterNames.map((name, i) => {
    const active = servers.filter(s => s.exporters?.some(e => e.name === name && e.active)).length;
    const short = name.substring(0, 2).toUpperCase();
    return { label: name.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()), short, key: name, active, total: serverCount, gradient: GRADIENTS[i % GRADIENTS.length] };
  }).filter(a => a.active > 0);
  const activeAgentTypeCount = exporterCounts.length;

  const openIncidents = incidentsData?.stats?.open ?? incidentList.filter(i => i.status === 'open').length;
  const secTotal24h = (securityEvents.critical || 0) + (securityEvents.high || 0) + (securityEvents.medium || 0) + (securityEvents.low || 0);

  const clearCache = async () => {
    try {
      await fetch('/api/dashboard/cache/clear', { method: 'POST' });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      toast('Dashboard cache cleared', true);
    } catch (e) { console.error(e); }
  };

  const ackIncident = async (id) => {
    try { await fetch(`/api/incidents/${id}/acknowledge`, { method: 'POST' }); setIncidentList(prev => prev.map(i => i.id === id ? { ...i, status: 'acknowledged' } : i)); toast(`Incident ${id} acknowledged`, true); }
    catch (e) { console.error(e); }
  };

  if (loading) return (
    <div className="flex items-center justify-center" style={{ height: 256 }}><div className="flex items-center gap-3 muted">
      <svg className="animate-spin w-5 h-5" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>
      Loading dashboard...</div>
    </div>
  );

  const toggleServices = () => {
    const next = !servicesExpanded;
    setServicesExpanded(next);
    try { localStorage.setItem('synoti:dash:servicesExpanded', String(next)); } catch (e) {}
  };

  const renderServices = () => {
    const unhealthySvcs = services.filter(s => s.status !== 'running' || s.health === 'unhealthy');
    const avgCpu = services.length > 0 ? Math.round(services.reduce((s, svc) => s + (svc.cpu || 0), 0) / services.length) : 0;
    const avgMem = services.length > 0 ? Math.round(services.reduce((s, svc) => s + (svc.memory || 0), 0) / services.length) : 0;
    const allHealthy = unhealthySvcs.length === 0 && services.length > 0;
    return (
      <Panel
        title="Services"
        sub={`${runningCount}/${totalCount} running · CPU avg ${avgCpu}% · RAM avg ${avgMem}%`}
        actions={
          <div className="flex items-center gap-2">
            {allHealthy && <Badge tone="green">All healthy</Badge>}
            {unhealthySvcs.length > 0 && <Badge tone="red">{unhealthySvcs.length} issue{unhealthySvcs.length > 1 ? 's' : ''}</Badge>}
            <Button variant="ghost" size="sm" onClick={toggleServices}>{servicesExpanded ? 'Hide' : 'Show all'}</Button>
          </div>
        }
        noPad
      >
        {servicesExpanded && (
          <div className="table-wrap">
            <table>
              <thead>
                <tr><th>Service</th><th>Status</th><th>CPU</th><th>RAM</th><th>Net I/O</th><th>Uptime</th></tr>
              </thead>
              <tbody>
                {services.map(svc => {
                  const sc = statusColors[svc.status] || { badge: '', dot: '#6b7280' };
                  const healthLabel = svc.health === 'healthy' ? '✓' : svc.health === 'unhealthy' ? '✗' : '·';
                  const healthColor = svc.health === 'healthy' ? 'var(--success)' : svc.health === 'unhealthy' ? 'var(--danger)' : 'var(--text-3)';
                  const uptimeStr = svc.uptime > 86400 ? `${Math.floor(svc.uptime / 86400)}d ${Math.floor((svc.uptime % 86400) / 3600)}h` : svc.uptime > 3600 ? `${Math.floor(svc.uptime / 3600)}h ${Math.floor((svc.uptime % 3600) / 60)}m` : svc.uptime > 60 ? `${Math.floor(svc.uptime / 60)}m` : `${svc.uptime}s`;
                  return (
                    <tr key={svc.name}>
                      <td><div className="flex items-center gap-1.5"><div style={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: sc.dot }} /><span style={{ color: 'var(--text-1)', fontWeight: 500 }}>{svc.name}</span></div></td>
                      <td><span className={`badge ${svc.status === 'running' ? 'green' : 'red'}`}>{svc.status}</span><span style={{ marginLeft: 6, color: healthColor }}>{healthLabel}</span></td>
                      <td><div className="flex items-center gap-1.5" style={{ minWidth: 90 }}><MetricBar value={svc.cpu} /><span className="mono">{svc.cpu}%</span></div></td>
                      <td><div style={{ minWidth: 110 }}><MetricBar value={svc.memory} /><div className="muted">{svc.memoryUsage} MB / {svc.memoryLimit} MB</div></div></td>
                      <td><span className="mono"><span style={{ color: 'var(--success)' }}>↓{(svc.networkRx || 0).toFixed(1)}</span> <span style={{ color: 'var(--warning)' }}>↑{(svc.networkTx || 0).toFixed(1)}</span></span></td>
                      <td><span className="mono">{uptimeStr}</span></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Panel>
    );
  };

  const incColumns = [
    { key: 'id', label: 'ID', render: (v) => <code>{String(v || '').slice(0, 8)}</code> },
    { key: 'severity', label: 'Severity', render: (v) => <SevBadge level={v} /> },
    { key: 'title', label: 'Title', render: (v, row) => <span style={{ color: 'var(--text-1)' }}>{v || row.description || 'No description'}</span> },
    { key: 'assigned_to', label: 'Assignee', render: (v) => v ? <span className="muted">{v}</span> : <span className="muted" style={{ opacity: 0.5 }}>—</span> },
    { key: 'status', label: 'Status', render: (v) => {
      const tone = v === 'resolved' ? 'green' : v === 'acknowledged' ? 'blue' : 'amber';
      return <Badge tone={tone}>{v}</Badge>;
    } },
    { key: 'created_at', label: 'Age', render: (v) => <span className="mono">{v ? formatTime(v) : ''}</span> },
  ];

  return (
    <div>
      <PageHeader
        crumbs={[{ label: 'SYNOTI' }, { label: 'Dashboard' }]}
        title="Dashboard"
        subtitle="Real-time security operations overview."
        actions={
          <div className="flex items-center gap-3 flex-wrap">
            <ChipGroup
              options={['1h', '6h', '24h', '7d', '30d'].map(v => ({ value: v, label: v }))}
              value={timeRange}
              onChange={setTimeRange}
            />
            <div className="select">
              <select value={refreshInterval} onChange={e => setRefreshInterval(Number(e.target.value))} title="Refresh interval">
                <option value="10">10s</option><option value="30">30s</option><option value="60">60s</option><option value="300">5m</option>
              </select>
            </div>
            <Button variant="ghost" size="sm" onClick={() => setAutoRefresh(!autoRefresh)} icon={autoRefresh ? 'refresh-cw' : 'circle-pause'}>
              {autoRefresh ? 'Auto' : 'Manual'}
            </Button>
            {enhancedTs && <span className="muted mono">Updated {formatTime(enhancedTs, { timeOnly: true })}</span>}
            <Button size="sm" onClick={clearCache} icon="refresh-cw">Refresh</Button>
          </div>
        }
      />

      <div className="panel section-gap" style={{ background: 'linear-gradient(135deg, rgba(124,58,237,0.18), rgba(6,182,212,0.10)), var(--glass)' }}>
        <div className="panel-body" style={{ display: 'flex', alignItems: 'center', gap: 24, flexWrap: 'wrap' }}>
          {logoUrl && <div style={{ flexShrink: 0 }}><img src={logoUrl} alt="SYNOTI" style={{ width: 72, height: 72, borderRadius: 16, objectFit: 'contain', background: 'rgba(255,255,255,0.06)', padding: 8 }} /></div>}
          <div style={{ flex: 1, minWidth: 240 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 20, fontWeight: 800, color: 'var(--text-1)' }}>See Everything.</span>
              <span style={{ color: 'var(--text-3)' }}>|</span>
              <span style={{ fontSize: 20, fontWeight: 800, color: 'var(--text-1)' }}>Know Everything.</span>
              <span style={{ color: 'var(--text-3)' }}>|</span>
              <span style={{ fontSize: 20, fontWeight: 800, color: 'var(--text-1)' }}>Act Intelligently.</span>
            </div>
            <p className="muted" style={{ marginTop: 6 }}>SYNOTI AIOps + SecOps Platform — Real-time monitoring, security operations, AI-powered analysis, RAG-enriched knowledge base.</p>
          </div>
        </div>
      </div>

      <KpiGrid>
        <KpiCard label="Active Incidents" value={Number(openIncidents)} countUp icon="siren" iconBg="rgba(248,113,113,0.10)" iconColor="#f87171"
          sub={<><span className="up"><Icon name="trending-up" size={12} /> {(incidentsData?.stats?.investigating ?? 0)}</span> investigating · {(incidentsData?.stats?.in_progress ?? 0)} in progress</>} />
        <KpiCard label="Servers" value={`${onlineServers}/${serverCount}`} icon="server" iconBg="rgba(96,165,250,0.10)" iconColor="#60a5fa"
          sub={`${serverCount - onlineServers} offline`} />
        <KpiCard label="Services" value={`${runningCount}/${totalCount}`} icon="layers" iconBg="rgba(52,211,153,0.10)" iconColor="#34d399"
          sub={`${totalCount - runningCount} not running`} />
        <KpiCard label="Workers" value={`${workers.online || 0}/${workers.total || 0}`} icon="cpu" iconBg="rgba(167,139,250,0.10)" iconColor="#a78bfa"
          sub={`${queues.tasks_pending || 0} tasks pending`} />
        <KpiCard label="Detections / 24h" value={Number(secTotal24h)} countUp icon="search" iconBg="rgba(251,191,36,0.10)" iconColor="#fbbf24"
          sub={`${securityEvents.critical || 0} critical · ${securityEvents.high || 0} high`} />
        <KpiCard label="Threats Blocked" value={Number(threatsBlocked.blocked_24h || 0)} countUp icon="shield-check" iconBg="rgba(16,185,129,0.10)" iconColor="#10b981"
          sub={`${threatsBlocked.isolated_active || 0} endpoints isolated`} />
        <KpiCard label="Mean Time to Respond" value={mttrStats || '—'} icon="timer" iconBg="rgba(52,211,153,0.10)" iconColor="#34d399"
          sub={`${mttrTrend.length} resolved days tracked`} />
      </KpiGrid>

      <div className="grid grid-2 section-gap">
        <Panel title="System Load" sub="Core host metrics" live>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 16 }}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
              <Gauge value={sysMetrics.cpu_usage || 0} label="CPU" />
              <div style={{ width: '100%' }}><MetricBar value={sysMetrics.cpu_usage || 0} color="bg-indigo-500" /></div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
              <Gauge value={sysMetrics.memory_usage || 0} label="Memory" />
              <div style={{ width: '100%' }}><MetricBar value={sysMetrics.memory_usage || 0} color="bg-violet-500" /></div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
              <Gauge value={sysMetrics.disk_usage || 0} label="Disk" />
              <div style={{ width: '100%' }}><MetricBar value={sysMetrics.disk_usage || 0} color="bg-cyan-500" /></div>
            </div>
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}><span className="muted">Network (24h)</span><span className="mono" style={{ color: 'var(--success)', fontWeight: 700 }}>{enhancedLoading ? '...' : formatBits(networkIO.ingress_bps)}</span></div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}><span className="muted">Disk I/O</span><span className="mono" style={{ color: '#a78bfa', fontWeight: 700 }}>{enhancedLoading ? '...' : formatBytes(diskIO.read_bps)}/s</span></div>
              <Sparkline data={diskHistory} dataKey="read" color="#8b5cf6" height={28} />
            </div>
          </div>
        </Panel>
        <Panel title="Network Traffic (24h)">
          <div className="chart-box sm">
            {enhancedLoading ? (
              <div className="muted" style={{height:"100%",display:"flex",alignItems:"center",justifyContent:"center",fontSize:12}}>Loading...</div>
            ) : networkIO24h.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={networkIO24h}>
                  <defs><linearGradient id="rxGrad" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#10b981" stopOpacity={0.3} /><stop offset="95%" stopColor="#10b981" stopOpacity={0} /></linearGradient><linearGradient id="txGrad" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#f59e0b" stopOpacity={0.3} /><stop offset="95%" stopColor="#f59e0b" stopOpacity={0} /></linearGradient></defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--grid-line)" />
                  <XAxis dataKey="time" tick={{ fontSize: 8 }} stroke="var(--text-3)" interval={Math.max(Math.floor(networkIO24h.length / 6), 1)} />
                  <YAxis tick={{ fontSize: 8 }} stroke="var(--text-3)" tickFormatter={v => formatBits(v)} />
                  <Tooltip formatter={v => formatBits(v) + 'ps'} />
                  <Area type="monotone" dataKey="rx_bps" stroke="#10b981" fill="url(#rxGrad)" strokeWidth={1.5} dot={false} isAnimationActive={false} />
                  <Area type="monotone" dataKey="tx_bps" stroke="#f59e0b" fill="url(#txGrad)" strokeWidth={1.5} dot={false} isAnimationActive={false} />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="muted" style={{height:"100%",display:"flex",alignItems:"center",justifyContent:"center",fontSize:12}}>No data</div>
            )}
          </div>
        </Panel>
      </div>

      <div className="section-gap">
        <DashboardIngestion />
      </div>

      <div className="grid main-3 section-gap">
        <Panel title="Incident Queue" sub={`${openIncidents} open`}>
          <DataTable
            columns={incColumns}
            rows={incidentList.slice(0, 9)}
            rowKey="id"
            searchable
            searchPlaceholder="Search incidents…"
            emptyText="No incidents"
          />
        </Panel>
        <Panel title="Recent Activity" noPad>
          {activityFeed.length === 0 ? (
            <div className="empty">No activity</div>
          ) : (
            activityFeed.slice(0, 8).map((act, i) => (
              <AlertItem
                key={i}
                icon={act.type === 'incident' ? 'alert-triangle' : 'zap'}
                iconBg={act.type === 'incident' ? 'rgba(248,113,113,0.15)' : 'rgba(251,191,36,0.15)'}
                iconColor={act.type === 'incident' ? '#f87171' : '#fbbf24'}
                title={act.message}
                time={act.timestamp ? formatTime(act.timestamp) : ''}
              />
            ))
          )}
        </Panel>
        <Panel title="Resource Pressure">
          <div className="list-row"><span className="lbl" style={{ flex: '0 0 130px', color: 'var(--text-2)', fontSize: 12.5 }}>CPU — host</span><div className="progress" style={{ flex: 1 }}><span style={{ width: `${sysMetrics.cpu_usage || 0}%`, background: (sysMetrics.cpu_usage || 0) > 90 ? '#f87171' : (sysMetrics.cpu_usage || 0) > 70 ? '#fb923c' : '#34d399' }} /></div><b className="mono" style={{ fontSize: 12 }}>{sysMetrics.cpu_usage || 0}%</b></div>
          <div className="list-row"><span className="lbl" style={{ flex: '0 0 130px', color: 'var(--text-2)', fontSize: 12.5 }}>Memory — host</span><div className="progress" style={{ flex: 1 }}><span style={{ width: `${sysMetrics.memory_usage || 0}%`, background: (sysMetrics.memory_usage || 0) > 90 ? '#f87171' : (sysMetrics.memory_usage || 0) > 70 ? '#fb923c' : '#34d399' }} /></div><b className="mono" style={{ fontSize: 12 }}>{sysMetrics.memory_usage || 0}%</b></div>
          <div className="list-row"><span className="lbl" style={{ flex: '0 0 130px', color: 'var(--text-2)', fontSize: 12.5 }}>Disk — host</span><div className="progress" style={{ flex: 1 }}><span style={{ width: `${sysMetrics.disk_usage || 0}%`, background: (sysMetrics.disk_usage || 0) > 90 ? '#f87171' : (sysMetrics.disk_usage || 0) > 70 ? '#fb923c' : '#34d399' }} /></div><b className="mono" style={{ fontSize: 12 }}>{sysMetrics.disk_usage || 0}%</b></div>
        </Panel>
      </div>

      <div className="grid grid-2 section-gap">
        <Panel title="Active Incidents" sub={`${incidentsLoading ? '…' : openIncidents} open`} noPad>
          {incidentsLoading ? (
            <div className="flex items-center justify-center py-6 gap-2">
              <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" strokeDasharray="32" strokeLinecap="round" className="opacity-25"/><path d="M12 2a10 10 0 0 1 10 10" stroke="currentColor" strokeWidth="3" strokeLinecap="round"/></svg>
              <span className="muted">Loading incidents...</span>
            </div>
          ) : incidentList.length === 0 ? (
            <div className="empty">No incidents</div>
          ) : (
            incidentList.slice(0, 5).map(inc => {
              const sc = severityColors[inc.severity] || severityColors.info;
              const isAck = inc.status === 'acknowledged', isRes = inc.status === 'resolved';
              return (
                <div key={inc.id} className="alert-item">
                  <div className="alert-icon" style={{ background: 'rgba(248,113,113,0.12)', color: sc.hex }}><Icon name="alert-triangle" size={16} /></div>
                  <div className="alert-body">
                    <div className="alert-title">
                      <span className="mono" style={{ fontSize: 11, color: 'var(--text-3)' }}>{inc.id?.slice(0, 8)}</span>
                      <SevBadge level={inc.severity} />
                      <Badge tone={isRes ? 'green' : isAck ? 'blue' : 'amber'}>{inc.status}</Badge>
                    </div>
                    <div className="alert-desc">{inc.title || inc.description || 'No description'}</div>
                    <div className="muted">{inc.created_at ? formatTime(inc.created_at) : ''}</div>
                  </div>
                  {canEdit && !isRes && !isAck && <Button variant="ghost" size="sm" onClick={() => ackIncident(inc.id)}>Ack</Button>}
                  {isAck && <span className="muted">Ack</span>}
                </div>
              );
            })
          )}
        </Panel>
        <Panel 
          title="Components" 
          sub={`${Object.values(components).filter(Boolean).length}/${Object.keys(components).length} active`}
          noPad
        >
          {healthLoading ? (
            <div className="flex items-center justify-center py-6 gap-2">
              <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" strokeDasharray="32" strokeLinecap="round" className="opacity-25"/><path d="M12 2a10 10 0 0 1 10 10" stroke="currentColor" strokeWidth="3" strokeLinecap="round"/></svg>
              <span className="muted">Loading components...</span>
            </div>
          ) : Object.entries(components).length === 0 ? (
            <div className="empty">No components</div>
          ) : (
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fill, minmax(155px, 1fr))', 
              gap: 6, 
              padding: '12px 16px' 
            }}>
              {Object.entries(components).map(([k, v]) => (
                <div key={k} style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 6, 
                  padding: '4px 8px', 
                  borderRadius: 4,
                  background: 'rgba(255,255,255,0.02)',
                  fontSize: 12
                }}>
                  <div style={{ 
                    width: 6, 
                    height: 6, 
                    borderRadius: '50%', 
                    background: v ? 'var(--success)' : 'var(--danger)',
                    flexShrink: 0
                  }} />
                  <span style={{ 
                    color: 'var(--text-2)', 
                    textTransform: 'capitalize',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }}>
                    {k.replace(/_/g, ' ')}
                  </span>
                </div>
              ))}
            </div>
          )}
        </Panel>
      </div>

      <DashboardServers
        servers={servers}
        serverCount={serverCount}
        exporterCounts={exporterCounts}
        activeAgentTypeCount={activeAgentTypeCount}
        totalAgentTypes={totalAgentTypes}
      />

      <div className="section-gap">
        <DashboardSources sources={sourceStatus} />
      </div>

      <div className="section-gap">
        <DashboardPerformance
          enhancedLoading={enhancedLoading}
          forecastData={forecastData}
          diskIO={diskIO}
          diskHistory={diskHistory}
          logVolume={logVolume}
          queueDepth={queueDepth}
          uptimeSummary={uptimeSummary}
          dbSizes={dbSizes}
          forecast={forecast}
          kafkaLag={kafkaLag}
        />
      </div>
      <div className="section-gap">
        <DashboardSecurity
          securityEvents={securityEvents}
          topAlerting={topAlerting}
          severityTimeline={severityTimeline}
          activityFeed={activityFeed}
        />
      </div>
      <div className="section-gap">
        {renderServices()}
      </div>
      <div className="section-gap">
        <DashboardAI
          aiStats={aiStats}
          chatopsStats={chatopsStats}
          mttrTrend={mttrTrend}
          aiEffectiveness={aiEffectiveness}
          severityTimeline={severityTimeline}
          incidentTimeline={incidentTimeline}
        />
      </div>

      <div className="panel">
        <div className="panel-body" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 8, height: 8, borderRadius: '50%', background: health?.status === 'healthy' ? 'var(--success)' : 'var(--danger)' }} />
          <span style={{ color: 'var(--text-2)', fontSize: 12 }}>Core API: <b style={{ color: 'var(--text-1)' }}>{health?.status || 'unreachable'}</b></span>
          {health?.timestamp && <span className="muted mono" style={{ marginLeft: 'auto' }}>{formatTime(health.timestamp, { timeOnly: true })}</span>}
        </div>
      </div>
    </div>
  );
}
